import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ReportCard } from '@/modules/common/components/reports/ReportCard.tsx';
import {
  useGenerateDistributionReportsMutation,
  useGenerateInvestorDistributionReportsMutation,
  useIssuerDividendEventByIdQuery,
} from '@/modules/issuer-dashboard/queries/dividends.tsx';
import { Alert, Button, ChartEmptyState, DropdownMenu, ModalContentWrapper, Spinner } from 'ui';
import { AlertBlueIcon } from 'ui/icons';
import { RegisterClass, ReportType, ReportWithUrlResponse } from 'api';

const GENERATION_LOCK_MINUTES = 25;
const GENERATION_LOCK_MS = GENERATION_LOCK_MINUTES * 60 * 1000;
const REPORTS_REFETCH_INTERVAL_MS = 10000;
const GENERATION_IN_PROGRESS_MESSAGE = `Reports are being generated. This may take up to 10 minutes.`;

const getLatestReportGeneratedAtTs = (reports: ReportWithUrlResponse[]): number | null => {
  let latestTs: number | null = null;

  reports.forEach(report => {
    if (!report.generated_at) {
      return;
    }

    const reportGeneratedAtTs = new Date(report.generated_at).getTime();
    if (Number.isNaN(reportGeneratedAtTs)) {
      return;
    }

    if (latestTs === null || reportGeneratedAtTs > latestTs) {
      latestTs = reportGeneratedAtTs;
    }
  });

  return latestTs;
};

interface DistributionReportsModalProps {
  dividendId: number;
  registerId: number;
  registerClass: RegisterClass;
  reports?: ReportWithUrlResponse[];
  open: boolean;
  onClose: () => void;
}

interface ActionLockState {
  startedAtTs: number;
  untilTs: number;
  message: string;
}

const generationLockByDividendId = new Map<number, ActionLockState>();

const createActionLock = (
  startedAtTs: number,
  durationMs: number,
  message: string,
): ActionLockState => ({
  startedAtTs,
  untilTs: startedAtTs + durationMs,
  message,
});

const getValidActionLock = (dividendId: number, nowTs: number): ActionLockState | null => {
  const lock = generationLockByDividendId.get(dividendId) ?? null;
  if (!lock || lock.untilTs <= nowTs) {
    if (lock) {
      generationLockByDividendId.delete(dividendId);
    }
    return null;
  }

  return lock;
};

export const DistributionReportsModal = ({
  open,
  onClose,
  dividendId,
  registerId,
  registerClass,
  reports,
}: DistributionReportsModalProps) => {
  const {
    data: dividendByIdData,
    isFetching: isFetchingReports,
    refetch: refetchReports,
  } = useIssuerDividendEventByIdQuery({ id: dividendId }, !open || !dividendId, {
    refetchInterval: open ? REPORTS_REFETCH_INTERVAL_MS : false,
  });
  const [actionLock, setActionLock] = useState<ActionLockState | null>(() => {
    if (!dividendId) {
      return null;
    }

    return getValidActionLock(dividendId, Date.now());
  });

  const currentReports = useMemo(
    () => dividendByIdData?.data?.reports ?? reports ?? [],
    [dividendByIdData?.data?.reports, reports],
  );
  const hasInvestorDistributionReport = currentReports.some(
    report => report.type === ReportType.InvestorDistribution,
  );
  const latestReportGeneratedAtTs = getLatestReportGeneratedAtTs(currentReports);
  const nowTs = Date.now();
  const isWithinGenerationWindow =
    latestReportGeneratedAtTs !== null && nowTs - latestReportGeneratedAtTs < GENERATION_LOCK_MS;
  const isServerBlockActive = !hasInvestorDistributionReport && isWithinGenerationWindow;
  const isActionLockActive = actionLock !== null && nowTs < actionLock.untilTs;
  const shouldDisableGenerate = isServerBlockActive || isActionLockActive;
  const showGeneratingAlert = isServerBlockActive || isActionLockActive;
  const defaultGeneratingMessage = GENERATION_IN_PROGRESS_MESSAGE;
  const resolvedGeneratingMessage = isActionLockActive
    ? actionLock?.message ?? defaultGeneratingMessage
    : defaultGeneratingMessage;

  useEffect(() => {
    const nextLock = getValidActionLock(dividendId, Date.now());
    setActionLock(prevLock => {
      if (
        prevLock?.startedAtTs === nextLock?.startedAtTs &&
        prevLock?.untilTs === nextLock?.untilTs &&
        prevLock?.message === nextLock?.message
      ) {
        return prevLock;
      }
      return nextLock;
    });
  }, [dividendId, open]);

  useEffect(() => {
    if (!actionLock) {
      return;
    }

    if (nowTs >= actionLock.untilTs) {
      generationLockByDividendId.delete(dividendId);
      setActionLock(null);
      return;
    }

    if (latestReportGeneratedAtTs !== null && latestReportGeneratedAtTs >= actionLock.startedAtTs) {
      generationLockByDividendId.delete(dividendId);
      setActionLock(null);
    }
  }, [actionLock, dividendId, latestReportGeneratedAtTs, nowTs]);

  const { mutateAsync: generateInvestorReports, isLoading: isGeneratingInvestorReports } =
    useGenerateInvestorDistributionReportsMutation();
  const { mutateAsync: generateDistributionReports, isLoading: isGeneratingDistributionReports } =
    useGenerateDistributionReportsMutation();
  const isGeneratingReports = isGeneratingInvestorReports || isGeneratingDistributionReports;

  const handleGenerateReports = useCallback(
    async (reportGroup: 'investor' | 'distribution') => {
      if (isGeneratingReports) {
        return;
      }

      const currentTs = Date.now();
      const hasInvestorDistribution = currentReports.some(
        report => report.type === ReportType.InvestorDistribution,
      );
      const latestGeneratedAtTs = getLatestReportGeneratedAtTs(currentReports);
      const isWithinWindow =
        latestGeneratedAtTs !== null && currentTs - latestGeneratedAtTs < GENERATION_LOCK_MS;
      const isServerBlockedAtClick = !hasInvestorDistribution && isWithinWindow;
      const isActionLockedAtClick = actionLock !== null && currentTs < actionLock.untilTs;
      const isGenerateBlocked = isServerBlockedAtClick || isActionLockedAtClick;

      if (isGenerateBlocked) {
        toast.error(resolvedGeneratingMessage);
        return;
      }

      try {
        const generateReports =
          reportGroup === 'investor' ? generateInvestorReports : generateDistributionReports;
        const result = await generateReports({
          id: dividendId,
          register_id: registerId,
        });
        if (result.isConflict) {
          const conflictLock = createActionLock(
            currentTs,
            GENERATION_LOCK_MS,
            defaultGeneratingMessage,
          );
          generationLockByDividendId.set(dividendId, conflictLock);
          setActionLock(conflictLock);
          await refetchReports();
          return;
        }
        const startedAtTs = currentTs;
        const nextLock = createActionLock(
          startedAtTs,
          GENERATION_LOCK_MS,
          defaultGeneratingMessage,
        );
        generationLockByDividendId.set(dividendId, nextLock);
        setActionLock(nextLock);
        await refetchReports();
      } catch {
        toast.error(defaultGeneratingMessage);
      }
    },
    [
      actionLock,
      defaultGeneratingMessage,
      currentReports,
      dividendId,
      generateDistributionReports,
      generateInvestorReports,
      isGeneratingReports,
      refetchReports,
      registerId,
      resolvedGeneratingMessage,
    ],
  );

  return (
    <ModalContentWrapper
      variant="right"
      title="Reports"
      headerAction={
        <DropdownMenu
          items={[
            {
              label: 'Generate Investor Distribution Reports',
              onClick: () => handleGenerateReports('investor'),
              disabled: isGeneratingReports || shouldDisableGenerate,
            },
            {
              label: 'Generate Distribution Reports',
              onClick: () => handleGenerateReports('distribution'),
              disabled: isGeneratingReports || shouldDisableGenerate,
            },
          ]}
          disabled={isGeneratingReports || shouldDisableGenerate}
          placement="left-start"
          containerClassName="!w-auto"
          className="!w-[360px]"
          button={
            <Button
              className="w-auto min-h-8 px-3 py-1 whitespace-nowrap outline-0 focus:outline-0"
              loading={isGeneratingReports}
              disabled={isGeneratingReports || shouldDisableGenerate}
            >
              <span className="flex items-center gap-2">Generate</span>
            </Button>
          }
        />
      }
      open={open}
      onClose={onClose}
    >
      {showGeneratingAlert && (
        <Alert icon={AlertBlueIcon} variant="neutral-2" className="mb-4">
          <div className="whitespace-normal">{resolvedGeneratingMessage}</div>
        </Alert>
      )}
      {isFetchingReports && currentReports.length === 0 && (
        <div className="flex justify-center items-center min-h-[120px]">
          <Spinner size="lg" />
        </div>
      )}
      {!isFetchingReports && currentReports.length === 0 && (
        <div className="flex justify-center items-center font-medium text-lg">
          <ChartEmptyState />
        </div>
      )}
      <div className="w-full flex flex-col gap-2">
        {currentReports.map(report => (
          <ReportCard
            key={report.id}
            report={report}
            canDelete={false}
            sendingRegisterClass={registerClass}
          />
        ))}
      </div>
    </ModalContentWrapper>
  );
};
